import { Injectable, OnDestroy, Injector } from '@angular/core';
import { SearchCondition } from 'src/app/shared/model/search-condition.model';
import { SearchCount } from 'src/app/shared/model/search-count.model';
import { SearchPage } from 'src/app/shared/model/search-page.model';
import { Observable, Subject, ReplaySubject, of, Subscription, EMPTY, throwError } from 'rxjs';
import { PageInfo } from 'src/app/shared/model/page-info.model';
// Rxjs
import { switchMap, tap, timeout, takeUntil, catchError, retry, retryWhen, delay, concatMap } from 'rxjs/operators';
import { API_SERVER_URL } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { HttpHeaderService } from '../http-header.service';
import { SearchTag } from '../../model/search-tag.model';
import { RequestManagerService } from '../request-manager.service';
import { ToastrService } from 'ngx-toastr';
// -----------------------------------------------------------------------------------------
export interface SearchParams {
  id: string;
  additionalParams: object;
  currentSearchCondition?: any;
}
export abstract class SearchService implements OnDestroy {
  allSkills: string[][] = [];
  protected subscriptions: Subscription = new Subscription();
  // 進行中の検索リクエストを管理
  protected currentSearchRequest$ = new Subject<void>();

  constructor(
    private header: HttpHeaderService, 
    private http: HttpClient,
    protected requestManager: RequestManagerService,
    private injector: Injector
  ) {
    const skillSub = this.requestGet('api/v1/skill_candidates').subscribe(res => {
      this.allSkills = res.body.skills;
    });
    this.subscriptions.add(skillSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.currentSearchRequest$.next();
    this.currentSearchRequest$.complete();
  }

  initCondition = {};
  initPage = {};
  initPerPage = {};
  initSearchIds = [];
  selfServiceName = '';

  // subject関連
  public search$ = new ReplaySubject<SearchParams>(1);
  public searchSub = this.search$.asObservable().pipe(
    switchMap(doSearchParams => {
      this.requestManager.cancelRequest('search'); // User-unit cancellation
      this.currentSearchRequest$.next(); // Service-unit cancellation
      
      return new Observable(observer => {
        setTimeout(() => {
          observer.next(doSearchParams);
          observer.complete();
        }, 100); // Delay before starting new request
      });
    })
  );

  public searchSubject = new Subject<any>();
  public searchState = this.searchSubject.asObservable();

  // 検索開始
  public searchStartSubject = new Subject<any>();
  public searchStartState = this.searchStartSubject.asObservable();

  // 検索リセット
  public searchResetSubject = new Subject<any>();
  public searchResetState = this.searchResetSubject.asObservable();

  // ローディング停止イベント
  public loadingStopSubject = new Subject<void>();
  public loadingStopState = this.loadingStopSubject.asObservable();

  // 抽象メソッド：サブクラスにて実装
  search(id?: string, sortParams?: object, currentSearchCondition?: any): Observable<any> {
    // サブクラスでのAPI呼び出しをインターセプト
    return this.doSearch(id, sortParams, currentSearchCondition).pipe(
      takeUntil(this.currentSearchRequest$), // キャンセル可能にする
      takeUntil(this.requestManager.getCancelSubject('search')), // ユーザー単位のキャンセル
      // リトライ機能を追加（2回までリトライ）
      retryWhen(errors => 
        errors.pipe(
          concatMap((error, index) => {
            // 404エラーまたは500エラー（validate_table_name_for_userエラー）の場合のみリトライ
            if ((error.status === 404 || error.status === 500) && index < 2) {
              console.warn(`検索リクエストが失敗しました。リトライ ${index + 1}/2:`, error);
              return of(error).pipe(delay(1000)); // 1秒待機してリトライ
            }
            // 2回失敗した場合、または対象外エラーの場合はエラーを投げる
            if ((error.status === 404 || error.status === 500) && index >= 2) {
              console.error('検索リクエストが2回失敗しました。エラーメッセージを表示します。');
              // グローバルなエラーハンドリングでトーストを表示
              this.showErrorToast();
            }
            return throwError(error);
          })
        )
      ),
      tap(res => {
        if (
          sessionStorage.getItem('searchPage' + this.selfServiceName) === null
        ) {
          // 初回読込：ページング情報がセッションに存在しない場合、レスポンス情報から作成→セッション保存
          this.setSearchCount(
            new SearchCount({
              startCount: res.body.pageInfo.start_count,
              endCount: res.body.pageInfo.end_count,
        
              perPage: this.initPerPage['perPage']
            })
          );
        }
      })
    );
  }

  abstract doSearch(id?: string, sortParams?: object, currentSearchCondition?: any): Observable<any>;

  // 検索条件を組み合わせハッシュを返す
  getSearchParameters(sortParams?: object, currentSearchCondition?: any) {
    const condition = currentSearchCondition ? currentSearchCondition.getParams() : this.getSearchCondition();
    const count = this.getSearchCount().getParams();
    const page = this.getSearchPage().getParams();
    const searchTags = { search_tag_ids: this.getSearchTagIds() };
    return Object.assign(condition, count, page, searchTags, sortParams);
  }

  getSearchParams(): any {
    const sessionStorageSearchParams = sessionStorage.getItem(
      `${this.constructor.name}SearchParams`
    );
    if (sessionStorageSearchParams) {
      return JSON.parse(sessionStorageSearchParams);
    }
  }

  // ページ表示に必要なpageInfoを返す
  getPageInfo(): PageInfo {
    return new PageInfo(this.getSearchCount(), this.getSearchPage());
  }
  // セッター（sessionStrageに保存）
  public setSearchCondition(searchCondition: SearchCondition): void {
    sessionStorage.setItem(
      'searchCondition' + this.selfServiceName,
      JSON.stringify(searchCondition)
    );
  }
  public setSearchCount(searchCount: SearchCount): void {
    if (this.selfServiceName === 'SearchOfferWorkerService') {
      this.selfServiceName = 'SearchOfferProjectService';
    }
    sessionStorage.setItem(
      'searchCount' + this.selfServiceName,
      JSON.stringify(searchCount)
    );
  }
  public setSearchPage(searchPage: SearchPage): void {
    if (this.selfServiceName === 'SearchOfferWorkerService') {
      this.selfServiceName = 'SearchOfferProjectService';
    }
    sessionStorage.setItem(
      'searchPage' + this.selfServiceName,
      JSON.stringify(searchPage)
    );
  }
  public setSearchTags(searhTagIds: Array<number>): void {
    if (this.selfServiceName === 'SearchOfferWorkerService') {
      this.selfServiceName = 'SearchOfferProjectService';
    }
    sessionStorage.setItem(
      'searchTagIds' + this.selfServiceName,
      JSON.stringify(searhTagIds)
    );
  }

  // ゲッター（sessionStrageから取得）
  public getSearchCondition(): SearchCondition {
    return new SearchCondition(
      JSON.parse(
        sessionStorage.getItem('searchCondition' + this.selfServiceName) ||
          JSON.stringify(this.initCondition)
      )
    );
  }
  public getSearchCount(): SearchCount {
    if (this.selfServiceName === 'SearchOfferWorkerService') {
      this.selfServiceName = 'SearchOfferProjectService';
    }
    return new SearchCount(
      JSON.parse(
        sessionStorage.getItem('searchCount' + this.selfServiceName) ||
          JSON.stringify(this.initPerPage)
      )
    );
  }
  public getSearchPage(): SearchPage {
    if (this.selfServiceName === 'SearchOfferWorkerService') {
      this.selfServiceName = 'SearchOfferProjectService';
    }
    return new SearchPage(
      JSON.parse(
        sessionStorage.getItem('searchPage' + this.selfServiceName) ||
          JSON.stringify(this.initPage)
      )
    );
  }
  public getSearchTagIds(): Array<number> {
    if (this.selfServiceName === 'SearchOfferWorkerService') {
      this.selfServiceName = 'SearchOfferProjectService';
    }
    return JSON.parse(
      sessionStorage.getItem('searchTagIds' + this.selfServiceName)
    );
  }

  // getとpostの重複記述を圧縮
  requestGet(requestDestination: string, params?: any): Observable<any> {
    const url = API_SERVER_URL + requestDestination;
    
    return this.http.get<any>(url, this.header.getHttpOptions(params)).pipe(
      timeout(30000)
    );
  }

  protected requestPost(requestDestination: string, params?: object): Observable<any> {
    const url = API_SERVER_URL + requestDestination;
    
    return this.http.post<any>(url, params, this.header.getHttpOptions()).pipe(
      timeout(10000), // タイムアウトを短縮してリソース使用量を削減
      catchError(error => {
        if (error.name === 'TimeoutError' && error.message.includes('Timeout')) {
          return EMPTY;
        }
        throw error;
      })
    );
  }

  updateActionLogClickOpenDetail(params) {
    const url =
      API_SERVER_URL + 'api/v1/action_log_for_detail/create_click_open_log';
    return this.http.post<any>(url, params, this.header.getHttpOptions());
  }

  // エラートースト表示のためのメソッド
  protected showErrorToast() {
    try {
      // Injectorを使用してToastrServiceを動的に取得
      const toastrService = this.injector.get(ToastrService);
      toastrService.error('検索時にエラーが発生しました。お手数ですが画面を更新してください');
    } catch (error) {
      // ToastrServiceが利用できない場合はalertにフォールバック
      console.warn('ToastrService not available, falling back to alert');
      alert('検索時にエラーが発生しました。お手数ですが画面を更新してください');
    }
    
    // ローディング停止イベントを発火
    this.loadingStopSubject.next();
  }
}
